/**
 * DTOs for the EnSan Steckbrief CRUD API (`/v1/ensan/steckbriefe`, RFC-004 §5,
 * GEN-690). Mirrors the backend Kotlin DTOs exactly.
 */
export interface SteckbriefDto {
    id: string;
    /** Printed document number ("SB-NNN"), minted server-side, immutable. */
    code: string;
    realEstateAssetId: string;
    name: string;
    /** Subset of the REA's buildings this Steckbrief covers. */
    buildingIds: string[];
    createdAt: string;
    createdBy: string;
    updatedAt: string | null;
    /**
     * The Steckbrief's Variants, newest first — only present when the list was
     * requested with `include=variants` (GEN-760). Absent means "not requested",
     * an empty array means "none saved".
     */
    variants?: SteckbriefVariantDto[];
}

/**
 * Create request. `buildingIds` must be a non-empty subset of the REA's
 * buildings; `name` defaults to the REA name on the backend when absent.
 */
export interface CreateSteckbriefRequest {
    realEstateAssetId: string;
    buildingIds: string[];
    name?: string;
}

/**
 * Request of `PATCH /v1/ensan/steckbriefe/{id}` — partial update, absent fields
 * keep their current value. `buildingIds`, when present, is re-validated as a
 * non-empty subset of the REA's buildings; the `code` is immutable.
 */
export interface UpdateSteckbriefRequest {
    name?: string;
    buildingIds?: string[];
}

// ---------------------------------------------------------------------------
// Variants (`/v1/ensan/steckbriefe/{id}/variants`, RFC-004 §5, GEN-692) and the
// stateless calculation preview (`POST /calculate`, GEN-696). Mirrors the
// backend Kotlin DTOs exactly — see
// dev-context/features/ensan-steckbrief/api-contracts.md.
// ---------------------------------------------------------------------------

export type SteckbriefKind = 'SALES' | 'FINAL';

/** `Measure` enum of the backend `ensan` module (EB-validated rule table v2). */
export type MeasureKey =
    | 'ROOF_TOP_CEILING_INSULATION'
    | 'FACADE_INSULATION'
    | 'BASEMENT_CEILING_INSULATION'
    | 'WINDOW_REPLACEMENT'
    | 'SUN_PROTECTION'
    | 'HEATING_RENEWAL'
    | 'PHOTOVOLTAICS'
    | 'HYDRAULIC_BALANCING'
    | 'NEW_PUMPS'
    | 'PIPE_INSULATION'
    | 'LED_LIGHTING'
    | 'VENTILATION_WITH_HEAT_RECOVERY';

/**
 * `RecommendedHeatingSystem` enum of the backend `ensan` module. Since GEN-780
 * admi no longer offers Bio-Gas: `BIO_GAS`, `BIO_GAS_WITH_SOLAR_THERMAL` and
 * `HEAT_PUMP_WITH_BIO_GAS` are RETIRED (never proposed, not offered as an
 * override) but stay in the type — a legacy stamp may still carry one.
 */
export type RecommendedHeatingSystemKey =
    | 'HEAT_PUMP'
    | 'HEAT_PUMP_WITH_BIO_GAS'
    | 'BIOMASS'
    | 'BIOMASS_WITH_SOLAR_THERMAL'
    | 'BIO_GAS'
    | 'BIO_GAS_WITH_SOLAR_THERMAL'
    | 'ELECTRIC_HEATING';

/** Source marker for values the user entered/edited (vs a Data Catalog key). */
export const USER_PROVIDED_SOURCE = 'USER_PROVIDED';

/**
 * Source marker of a value saved at its default — mirrors the backend
 * `VariantDataStamp.DEFAULT`. GEN-801 introduced it for the fixed 35 % window
 * area share; since GEN-802 it means "equals the model default configured for
 * the submitted Gebäudetyp" (the backend `SteckbriefAssumptionCatalog`).
 * GEN-801-era stamps (DEFAULT 35, no `assumptionDefaults` section) keep their
 * meaning. Never carried into the next Variant: open mode drops it and the
 * form re-seeds the current default (see `dropAssumptionDefaults`).
 */
export const DEFAULT_SOURCE = 'DEFAULT';

/**
 * One prefilled field. `source` is the Data Catalog key the value was resolved
 * from, `USER_PROVIDED` when carried from the newest previous Variant, or
 * `null` for an empty field without a retrieval source. A server prefill never
 * carries `DEFAULT` — only an open-mode prefill
 * synthesized from a stamp can, and the form drops it (see
 * `normalizeVariantPrefill`).
 */
export interface PrefilledVariantField {
    value: unknown;
    source: string | null;
}

/** Response of `POST /variants/prefill` — per form key, in field-mapping order. */
export interface VariantPrefillDto {
    kind: SteckbriefKind;
    fields: Record<string, PrefilledVariantField>;
}

/** Engine-proposed vs advisor-selected heating system (HEATING_RENEWAL). */
export interface HeatingSystemSelection {
    proposed: string | null;
    selected: string | null;
}

/** Measure payload of a Variant save — from the client's `/calculate` run. */
export interface VariantMeasures {
    proposed: string[];
    selected: string[];
    heatingSystem?: HeatingSystemSelection | null;
    /**
     * Measure keys the advisor flagged as Prio (GEN-754). `null`/absent = no
     * override — the computed top-N-by-impact default applies. Non-null
     * (including empty) = the explicit Prio set exactly as the advisor left it;
     * entries outside `selected` are meaningless and ignored leniently.
     */
    priorities?: string[] | null;
    /**
     * The advisor's reason per measure whose classification deviates from the
     * system proposal, keyed by measure key. Internal only — stamped with the
     * Variant, never printed. `null`/absent = no deviations.
     */
    reasons?: Record<string, string> | null;
}

/**
 * Request of `POST /variants` — saves a new immutable Variant. `values` is
 * keyed by field-mapping form keys; keys outside the mapping are rejected.
 * The data stamp is built server-side (ADR-011) — client sources are never sent.
 */
export interface CreateVariantRequest {
    name: string;
    kind: SteckbriefKind;
    values: Record<string, unknown>;
    measures?: VariantMeasures | null;
    /**
     * The PV estimate the form displayed (GEN-799) — the server loads that row
     * (it must belong to the Steckbrief and match the hash of the submitted
     * values) and stamps it; it never re-estimates at save. Null when the form
     * displayed none (PV not selected, or no estimate row yet).
     */
    pvEstimateId?: string | null;
}

/** One stamped input field of a saved Variant (value + provenance, ADR-011). */
export interface StampedField {
    value: unknown;
    source: string | null;
}

/**
 * Key under `StampedMeasures.overrides` marking an advisor-overridden Prio set
 * (GEN-754) — mirrors the backend `VariantDataStamp.PRIORITIES_OVERRIDE_KEY`.
 */
export const PRIORITIES_OVERRIDE_KEY = 'priorities';

/**
 * Key under `StampedMeasures.overrides` marking an advisor-overridden heating
 * system — the open-mode form seeds its heating override only when this key is
 * present (GEN-765), so an engine-followed selection stays engine-followed.
 */
export const HEATING_SYSTEM_OVERRIDE_KEY = 'heatingSystem';

/** Measures as persisted in the stamp incl. advisor-override provenance. */
export interface StampedMeasures {
    proposed: string[];
    selected: string[];
    heatingSystem: HeatingSystemSelection | null;
    /**
     * The submitted Prio set verbatim (`VariantMeasures.priorities`); absent/null
     * on every stamp saved before GEN-754 — those generate with the computed
     * default flags, byte-identical to before.
     */
    priorities?: string[] | null;
    /**
     * Every advisor deviation from the proposal, marked `USER_PROVIDED` —
     * per-measure selection toggles, `heatingSystem`, and a Prio set deviating
     * from the computed top-N default under `PRIORITIES_OVERRIDE_KEY`.
     */
    overrides: Record<string, string>;
    /** The submitted per-measure reasons (trimmed, blanks dropped); absent on older stamps. Not printed. */
    reasons?: Record<string, string> | null;
}

/**
 * The PV estimate as persisted in the stamp (GEN-799): the `/calculate`
 * object the form displayed at save time plus WHEN it was stamped. Absent on
 * every stamp saved before the feature — generation then replays PV as
 * excluded (reason LEGACY).
 */
export type StampedPvEstimate = PvEstimateDto & { stampedAt: string };

/**
 * The level an assumption default was resolved at (GEN-802) — mirrors the
 * backend `AssumptionLevel` enum: the general BASE value, or the row of the
 * submitted Gebäudetyp (even when that row's value equals the base).
 */
export type AssumptionLevel = 'BASE' | 'BUILDING_FUNCTION';

/** One resolved assumption default (GEN-802): the raw value and its level. */
export interface AssumptionDefaultDto {
    value: number;
    level: AssumptionLevel;
}

/**
 * Response of `GET /v1/ensan/steckbriefe/assumption-defaults` (GEN-802): the
 * model defaults the form seeds, per form key. Rows arrive RESOLVED — every
 * row carries every key, inherited keys at level BASE — so the client lookup
 * is a plain row pick (see `resolveAssumptionDefault`). Codes and raw numbers
 * only (ADR-018).
 */
export interface SteckbriefAssumptionDefaultsDto {
    /** Form key → default for a missing or unlisted Gebäudetyp. */
    base: Record<string, AssumptionDefaultDto>;
    /** Raw ADV building-function code → form key → default. */
    byBuildingFunction: Record<string, Record<string, AssumptionDefaultDto>>;
}

/** An assumption default as stamped at save time (GEN-802) — the shape of [AssumptionDefaultDto]. */
export interface StampedAssumptionDefault {
    value: number;
    level: AssumptionLevel;
}

/** Per-field provenance stamp of a saved Variant — built server-side only. */
export interface VariantDataStamp {
    fields: Record<string, StampedField>;
    measures?: StampedMeasures | null;
    /** The stamped PV estimate (GEN-799); absent/null on pre-feature stamps. */
    pvEstimate?: StampedPvEstimate | null;
    /**
     * The model defaults resolved for the submitted Gebäudetyp at save time
     * (GEN-802), per form key — audit only, generation never reads it. The
     * backend writes it on EVERY save, so absent/null marks a stamp saved
     * before GEN-802 (the legacy marker of `stampPrefillFields`).
     */
    assumptionDefaults?: Record<string, StampedAssumptionDefault> | null;
}

/** A saved (immutable) Steckbrief Variant. */
export interface SteckbriefVariantDto {
    id: string;
    steckbriefId: string;
    name: string;
    /**
     * Per-Steckbrief running number (GEN-786): 1, 2, … in save order, never
     * reused — the printed Dok-Nr. suffix (see [variantDocumentNumber]).
     * OPTIONAL until the backend change deploys: absent means unknown.
     */
    number?: number;
    kind: SteckbriefKind;
    createdAt: string;
    createdBy: string;
    dataStamp: VariantDataStamp;
    /** Set once the PDF has been generated (T14). */
    documentInstanceId: string | null;
    /**
     * Google Drive webViewLink of the generated PDF. Being added by a parallel
     * backend PR — OPTIONAL until that deploys: consume defensively (absent/null
     * means no link is known; `documentInstanceId` remains the generated flag,
     * and a fresh link always comes from the generate response or a refetch).
     */
    documentUrl?: string | null;
}

/**
 * Response of `POST /v1/ensan/steckbriefe/{id}/variants/{variantId}/generate`
 * (GEN-698): the generated PDF linked to its Variant.
 */
export interface VariantDocumentDto {
    variantId: string;
    steckbriefId: string;
    documentInstanceId: string;
    /** Google Drive webViewLink of the uploaded PDF. */
    documentUrl: string;
    generatedAt: string;
    /**
     * Warning-level assumptions of the generation's replay (GEN-700 D4), e.g.
     * LOOKALIKE_REFERENCE_CHANGED — the document is generated regardless. Absent on an older backend.
     */
    warnings?: AssumptionDto[];
}

/**
 * Request of `POST /v1/ensan/steckbriefe/{id}/document-preview` (GEN-762): the
 * `/calculate` request fields 1:1 plus the kind to print — the form previews
 * the document from exactly the state it would save, before anything is saved.
 * Nothing is persisted server-side; the response streams the PDF.
 */
export interface PreviewSteckbriefDocumentRequest extends CalculateSteckbriefRequest {
    /** Which of the two Steckbrief kinds to print (header label + file-name segment). */
    kind: SteckbriefKind;
}

/** Response of the document preview: the PDF blob + the server's attachment file name. */
export interface SteckbriefDocumentPreview {
    blob: Blob;
    /** From the `Content-Disposition` header; null when the header is missing/unparsable. */
    fileName: string | null;
}

/** Request of `POST /calculate` — stateless pipeline re-run for the live preview. */
export interface CalculateSteckbriefRequest {
    /** Raw form values keyed by the field-mapping form keys. */
    values: Record<string, unknown>;
    /** Advisor-selected measures; null/absent defaults to the engine's proposal. */
    selectedMeasures?: MeasureKey[] | null;
    /** Advisor override of the proposed heating system. */
    heatingSystemOverride?: RecommendedHeatingSystemKey | null;
    /**
     * Advisor override of WHICH selected measures carry the Prio flag (GEN-754).
     * `null` = no override: the computed top-N-by-impact default applies,
     * byte-identical to the un-overridden response. Non-null — including empty —
     * is the explicit, sticky Prio set: the effective flags are its intersection
     * with the selected set; entries outside it are ignored leniently. Echoed as
     * `SteckbriefCalculationResultDto.priorityOverride`; the effective flags land
     * in the proposals' `prio` and in `priorityMeasures`.
     */
    priorityOverride?: MeasureKey[] | null;
    /**
     * The id of the PV estimate the form is displaying (GEN-799): an opened
     * Variant's `dataStamp.pvEstimate.estimateId`, in create mode the
     * `estimateId` of the previous `/calculate` response, else null. The server
     * uses it only when the row belongs to the Steckbrief, is READY and its
     * `inputsHash` equals the hash of the submitted values; otherwise it falls
     * back to the newest READY row for that hash, else a terminal FAILED row,
     * else it computes the estimate now — synchronously, inside `/calculate`.
     */
    pvEstimateId?: string | null;
    /**
     * The Variant kind the form calculates for (GEN-806). On SALES without an
     * entered NGF (`netFloorArea`) or BGF (`grossFloorArea`) the backend
     * estimates the BGF from the CityGML building model and derives NGF =
     * 0.85 × BGF — reported in the assumptions, never written into the form.
     * FINAL and an absent kind never estimate.
     */
    kind?: SteckbriefKind | null;
}

// ---------------------------------------------------------------------------
// PV investment from the PV Calculator (GEN-799) — see the api-contracts.md
// "PV estimate" section. Language-agnostic (ADR-018): enum names + raw numbers.
// ---------------------------------------------------------------------------

/**
 * Backend `PvEstimateStatus`: READY = priced; FAILED = terminal or transient
 * failure (see `reason`); DISABLED = feature flag off; LEGACY = only inside a
 * stamp saved before the feature (never in a `/calculate` response). The
 * estimate is computed synchronously inside `/calculate` (and the refresh), so
 * there is no in-progress state.
 */
export type PvEstimateStatus = 'READY' | 'FAILED' | 'DISABLED' | 'LEGACY';

/**
 * Backend `PvEstimateUnavailableReason` — set only while the status is not
 * READY. CALCULATOR_UNAVAILABLE, CALCULATOR_REJECTED, NO_ROOF_POTENTIAL,
 * NO_SUITABLE_ROOF and NO_FOOTPRINT are retryable by the advisor (the refresh
 * action); DISABLED/LEGACY offer nothing to do.
 */
export type PvEstimateUnavailableReason =
    | 'NO_FOOTPRINT'
    | 'NO_ROOF_POTENTIAL'
    | 'NO_SUITABLE_ROOF'
    | 'CALCULATOR_REJECTED'
    | 'CALCULATOR_UNAVAILABLE'
    | 'DISABLED'
    | 'LEGACY';

/** Backend `PvRoofSource`: where the roof geometry of the PV design came from. */
export type PvRoofSource = 'GOOGLE_SOLAR' | 'ADMI_SOLAR';

/**
 * The PV estimate of one `/calculate` run (GEN-799) — also the response of
 * `POST …/pv-estimate/refresh`. Null on the calculation result only when
 * PHOTOVOLTAICS is not in the effective selection.
 */
export interface PvEstimateDto {
    /** The stored row's id for READY and FAILED; null for DISABLED (no row). */
    estimateId: string | null;
    /** Hash of (buildings, buildingFunction, annualUsage, energyPrice, policy) — the server-side cache key. */
    inputsHash: string;
    status: PvEstimateStatus;
    reason: PvEstimateUnavailableReason | null;
    roofSource: PvRoofSource | null;
    designKwp: number | null;
    batteryKwh: number | null;
    /** Whole EUR, gross (before funding). */
    investmentEur: number | null;
    /** The consumption sent to the calculator; null = estimated by the calculator. */
    annualUsageKwh: number | null;
    annualUsageEstimated: boolean | null;
    /** €/kWh used for the ROI sizing. */
    energyPrice: number | null;
    /** e.g. "ops-2026-04". */
    costModel: string | null;
    /** "roi". */
    optimizer: string | null;
    /** Buildings whose roof was part of the design. */
    buildingsPriced: number | null;
    /** Buildings of the Steckbrief subset. */
    buildingsTotal: number | null;
    /** ISO instant. */
    computedAt: string | null;
}

/** Request of `POST /v1/ensan/steckbriefe/{id}/pv-estimate/refresh` — the same values map as `/calculate`. */
export interface RefreshPvEstimateRequest {
    values: Record<string, unknown>;
}

/**
 * Language-agnostic rationale of one proposed measure (GEN-758): the stable
 * key of the rule that fired (backend `MeasureRationale` enum) plus its raw,
 * unformatted params (the rule table's threshold constants, sourced backend-
 * side from RecommendationConstants so they can never desync from what the
 * rules gate on). The client holds the message catalogs for both UI languages
 * (see `measureRationale` in steckbrief-calculation-messages.ts).
 */
export interface MeasureRationaleDto {
    /**
     * Stable rationale key — deliberately `string`, not a literal union: the
     * backend enum is additive and the client falls back to rendering the raw
     * key, so new keys must flow through without a frontend release.
     */
    code: string;
    /** Raw interpolation values — numbers as JSON numbers; null/absent when the message has none. */
    params?: Record<string, unknown> | null;
}

/** One proposed measure as presented to the Variant form. */
export interface ProposedMeasureDto {
    measure: MeasureKey;
    /**
     * German print label (measure chip). Print/template contract — since
     * GEN-758 the UI renders the code-based i18next translation instead
     * (see `measureLabel` in steckbrief-variant-fields.ts).
     */
    chipLabel: string;
    /**
     * Why the rule proposed this measure (tooltip), as stable key + raw params
     * (GEN-758) — translated and locale-formatted client-side in BOTH UI
     * languages. The heating-system choice reason is keyed by `heatingSystem`
     * (1:1 per system), and the fallback note derives from `usedFallbacks`.
     */
    rationale: MeasureRationaleDto;
    /**
     * Prio flag (template v1 Prio chips): the engine's rule-based default —
     * or, when the request carries a priority override (GEN-754), the override
     * intersected with the selected set.
     */
    prio: boolean;
    /**
     * The engine's own rule-based Prio flag ("Steckbrief recommendations v2") —
     * never follows the advisor's override, so the system column shows the
     * proposal unchanged next to the classification.
     */
    systemPrio: boolean;
    /** True when the rule resolved unknown facts through documented assumptions. */
    usedFallbacks: boolean;
    /** Set only on the heating-renewal measure. */
    heatingSystem: RecommendedHeatingSystemKey | null;
    /**
     * The year the measure falls due (QA feedback round) — set only on
     * `HEATING_RENEWAL` when the (known or derived) heating is younger than 20
     * years: heating year + 20. The selector labels the heat pump e.g.
     * "Wärmepumpe (2035)". Optional: absent from backends before the feature.
     */
    dueYear?: number | null;
}

export interface RangeDto {
    low: number;
    high: number;
}

export interface FundingHeadlineRangeDto {
    programme: string;
    minPercent: number | null;
    maxPercent: number | null;
    /** True when the headline comes from a conditional match — never firm. */
    conditional: boolean;
}

/**
 * One assumption the engines ACTUALLY applied in a `/calculate` run (GEN-756).
 * Honesty is the feature: entries are collected at the firing sites — an entry
 * appears only when its code path fired in that calculation, never as static
 * boilerplate.
 *
 * Language-agnostic transport (GEN-758): the entry carries NO display text —
 * the client holds the message catalogs for both UI languages, keyed by
 * `code`, interpolates the raw `params` with per-locale number formatting, and
 * renders an honest code+params fallback for a code it does not know yet (see
 * `assumptionMessage` in steckbrief-calculation-messages.ts).
 */
export interface AssumptionDto {
    /**
     * Stable machine code (backend `AssumptionCode` enum, English identifier).
     * Deliberately `string`, not a literal union: the enum is ADDITIVE and the
     * client renders unknown codes through the honest fallback, so new backend
     * codes must flow through without a frontend release.
     */
    code: string;
    /**
     * Raw, unformatted interpolation values of the client message: numbers as
     * JSON numbers (the client formats per locale — de comma, en point),
     * enum-ish values as their stable enum names (e.g. an `EnergyCarrier`).
     * The VALUES stay backend-owned: they are sourced from the same constants
     * the engines compute with, so they can never desync from the arithmetic.
     * Null/absent when the message carries no values.
     */
    params?: Record<string, unknown> | null;
    /** The Variant form key the assumption substituted or reinterpreted, when tied to one. */
    affectedFormKey?: string | null;
    /** The proposed measure whose rule applied the assumption, for rule-level entries. */
    measure?: string | null;
}

/**
 * Backend `PrognosisMetric` enum names (GEN-788) — the three savings metrics of
 * the Prognose panel; `PROGNOSIS_METRICS.metric` in steckbrief-variant-fields.ts
 * maps each onto its form keys.
 */
export type PrognosisMetricKey = 'ENERGY_SAVING_POTENTIAL' | 'CO2_SAVING' | 'COST_SAVING' | 'INVESTMENT_COST_PER_SQM';

/**
 * How much weight a prognosis warning takes from a number (GEN-788) — the
 * prefixes of the calibrated sheet's warning texts: WARNING = "YES —" (the
 * inputs sit outside what the reference set supports), CHECK = "CHECK —"
 * (plausible, but verify the inputs), SUPPRESSED = "CO2 SUPPRESSED —" (the
 * metric is deliberately not predicted).
 */
export type PrognosisWarningSeverity = 'WARNING' | 'CHECK' | 'SUPPRESSED';

/**
 * One warning of the energy/CO₂/cost prognosis model (GEN-788, "EnSan PA prediction
 * calculator"): the sheet's applicability checks, evaluated per `/calculate`
 * run. Language-agnostic like [AssumptionDto] (ADR-018): a stable code plus
 * RAW params (numbers unformatted, enum-ish values as their enum names) — the
 * client holds both catalogs (see `warningTexts` in
 * steckbrief-calculation-messages.ts). Shown in the Variant form only — never
 * printed, never stamped.
 */
export interface PrognosisWarningDto {
    /**
     * Backend `PrognosisWarningCode` enum name. Deliberately `string`, not a
     * literal union: the enum is additive and the client renders an unknown code
     * through the honest code + params fallback.
     */
    code: string;
    severity: PrognosisWarningSeverity;
    /**
     * The metrics the warning questions — for SUPPRESSED, the metrics it removes
     * (the Prognose panel then says "not predicted" instead of "inputs missing").
     * Empty for a warning that concerns only the plausibility checks.
     */
    affectedMetrics: PrognosisMetricKey[];
    /**
     * Raw interpolation values — the live value and the threshold the engine
     * tested, sourced from the backend constants at the firing site.
     */
    params?: Record<string, unknown> | null;
    /** The Variant form key whose input the warning is about, when tied to one. */
    affectedFormKey?: string | null;
    /** The measure the warning is about (the heat-pump switch), when tied to one. */
    measure?: string | null;
}

/**
 * Which H1 rule of the prognosis model applied (GEN-788): FOSSIL = the
 * renovation installs a heat pump; DISTRICT = Fernwärme kept; RENEWABLE =
 * envelope work with the generator kept (also Erdgas without a heat pump).
 */
export type PrognosisBranchKey = 'FOSSIL' | 'DISTRICT' | 'RENEWABLE';

/** Why the CO₂ saving is not predicted for a building (GEN-788). */
export type Co2SuppressionReasonKey =
    | 'DISTRICT_NETWORK_FACTOR_UNKNOWN'
    | 'WOOD_BASELINE_TOO_SMALL'
    | 'WOOD_HEAT_PUMP_STRADDLES_ZERO'
    | 'CARRIER_NOT_MODELLED'
    | 'HEATING_SYSTEM_NOT_MODELLED';

/**
 * The derivation behind the prognosis (GEN-788), for the read-only "Prediction
 * model" block — rounded at the response boundary (kWh 0 decimals, kWh/m²a,
 * t and percent 1 decimal, the use ratio 2, € 0 decimals, the cost saving to
 * the nearest 100 €). Null fields are values the run could not compute (no NGF,
 * no meters, a suppressed CO₂, no heat price). Since the sheet's v6 the cost
 * saving is the calibrated H3 as well (see the cost fields at the end).
 */
export interface PrognosisDetailsDto {
    branch: PrognosisBranchKey | null;
    heatPumpSwitch: boolean;
    /** roof + facade + basement ceiling + max(1, windows), 1–9 (the window score is at least 1). */
    envelopeScore: number | null;
    roofScore: number | null;
    facadeScore: number | null;
    basementCeilingScore: number | null;
    windowScore: number | null;
    /** Endenergiebedarf estimate from NGF and the envelope score (not the meter), kWh/a. */
    energyDemandEstimateKwh: number | null;
    /** Its heat share, kWh/a. */
    heatDemandEstimateKwh: number | null;
    /** Its building-electricity share, kWh/a. */
    buildingElectricityEstimateKwh: number | null;
    /** Estimated heat demand ÷ NGF, kWh/(m²a) — the district rule's driver. */
    specificHeatDemandEstimate: number | null;
    /** (metered heat + electricity) ÷ the demand estimate. */
    useRatio: number | null;
    /** f_heat of the heat carrier, kg CO₂e/kWh. */
    heatEmissionFactor: number | null;
    /** CO₂ Ist of the demand estimate, t/a. */
    co2BaselineTons: number | null;
    /** Point estimate of the energy saving, percent — NOT the midpoint of the band. */
    energySavingPotentialPoint: number | null;
    /** Point estimate of the CO₂ saving, t/a — NOT the midpoint of the band. */
    co2SavingPoint: number | null;
    co2SuppressionReason: Co2SuppressionReasonKey | null;
    /**
     * The EFFECTIVE heat tariff p_heat, €/kWh (sheet v6 Calculator B29): the
     * advisor's `heatPrice` when valid, else the carrier's EVEBI default —
     * null when neither exists (a carrier outside the model, no heat price).
     */
    heatPriceEurPerKwh: number | null;
    /** The EFFECTIVE electricity tariff p_elec, €/kWh: the advisor's `electricityPrice`, else the EVEBI default. */
    electricityPriceEurPerKwh: number;
    /**
     * Energiekosten Ist, €/a (Calculator B10): heat demand estimate × p_heat +
     * building-electricity estimate × p_elec — the demand ESTIMATE, never the meter.
     */
    energyCostEstimateEur: number | null;
    /** Point estimate of the cost saving as a share of those energy costs, percent (Calculator B43). */
    costSavingSharePercent: number | null;
    /** Point estimate of the cost saving, €/a, rounded to the nearest 100 € — may be negative. */
    costSavingPoint: number | null;
    /**
     * The energy costs ÷ the total metered energy, €/kWh (Calculator B48, the
     * PRICE check's value) — null unless both meters are present.
     */
    impliedPriceEurPerKwh: number | null;
}

/**
 * What a measure's investment cost scales with (backend `InvestmentBasis`,
 * GEN-780): the quantity of one measure line is the EFFECTIVE value of that
 * input — the same lattices the engine uses elsewhere (window area / NGF incl.
 * the 0.85 × BGF fallback) plus the stamped engine input `heatPumpPower`.
 * `FLAT_RATE` is a fixed amount charged once per Steckbrief (the ventilation):
 * quantity 1, always PRICED — the UI shows it as "pauschal", without a
 * quantity.
 */
export type InvestmentBasisKey =
    | 'FOOTPRINT_AREA'
    | 'FACADE_SURFACE_AREA'
    | 'WINDOW_SURFACE_AREA'
    | 'NET_FLOOR_AREA'
    | 'FLAT_RATE'
    | 'HEAT_PUMP_POWER'
    /** The PV line (GEN-799): quantity = the design's kWp, rate = investment ÷ kWp; no form input behind it. */
    | 'PV_PEAK_POWER';

/**
 * DIN V 18599-10 Tabelle 5 usage profile of the derived hourly heat consumption
 * (backend `HeatingUsageProfile`, GEN-780) — the `usageProfile` param of the
 * HEAT_PUMP_POWER_ESTIMATED / HEATING_USAGE_PROFILE_DEFAULTED assumptions. The
 * backend resolves it from the ADV building function (unmapped or missing →
 * OTHER_LOUNGE) and sends its yearly heating hours alongside as `heatingHours`;
 * the client only labels the code.
 */
export type HeatingUsageProfileKey =
    | 'OFFICE'
    | 'MEETING'
    | 'CLASSROOM'
    | 'LECTURE_HALL'
    | 'BED_ROOM'
    | 'HOTEL_ROOM'
    | 'OTHER_LOUNGE'
    | 'INDUSTRIAL_HALL'
    | 'AUDIENCE_AREA'
    | 'MUSEUM'
    | 'LIBRARY_READING_ROOM'
    | 'SPORTS_HALL'
    | 'LABORATORY'
    | 'MEDICAL_PRACTICE';

/**
 * Pricing outcome of one measure line (GEN-780): `PRICED` carries a cost;
 * `QUANTITY_UNKNOWN` = the basis input is missing (null or ≤ 0);
 * `NOT_PRICED` = the measure has no unit rate (the GEN-777 retired measures).
 * A set total is null as soon as ONE of its measures is not PRICED — never an
 * understated partial sum — EXCEPT `NOT_AVAILABLE` (GEN-799): the PV line
 * whose PV Calculator estimate failed (or is disabled) carries no cost and is
 * simply left out of the totals (the reason comes from `pvEstimate.reason`).
 */
export type MeasureInvestmentStatus = 'PRICED' | 'QUANTITY_UNKNOWN' | 'NOT_PRICED' | 'NOT_AVAILABLE';

/**
 * One line of the item-by-item investment estimate (GEN-780) — language-
 * agnostic (ADR-018): codes + raw numbers, the client labels and
 * locale-formats them. cost = quantity × unit-rate range, whole EUR, unrounded
 * per line (only the set totals round to 100,000 €).
 */
export interface MeasureInvestmentDto {
    measure: MeasureKey;
    /** Null for a measure without a rate (NOT_PRICED). */
    basis: InvestmentBasisKey | null;
    /** The effective quantity in the basis' unit; null when unknown or not priced. */
    quantity: number | null;
    /** €/unit, low end of the rate range (sheet "Default minimum"). */
    unitCostLow: number | null;
    /** €/unit, high end of the rate range (sheet "Default minimum + 30 %"). */
    unitCostHigh: number | null;
    /** Whole EUR; PRICED lines only. */
    cost: RangeDto | null;
    status: MeasureInvestmentStatus;
    /** Membership in the EFFECTIVE Prio set (the same flags as the proposals'). */
    prio: boolean;
}

/**
 * The orientation-band category a building function resolves to (English
 * code, the client translates). `EVENT_VENUE` is also the DEFAULT fallback.
 */
export type OrientationBandCategory =
    | 'ADMINISTRATION'
    | 'SCHOOL'
    | 'SCHOOL_WITH_SPORTS_HALL'
    | 'KITA'
    | 'SPORTS_HALL'
    | 'FIRE_STATION'
    | 'COMMUNITY_CENTRE'
    | 'EVENT_VENUE'
    | 'INDOOR_POOL';

/**
 * The class limits in kWh/(m²a) — displayed as Gut `< gutUpper`, Solide
 * `gutUpper – (maessigLower − 1)`, Mäßig `maessigLower – maessigUpper`,
 * Kritisch `> maessigUpper` (the BMWK doc's integer bands).
 */
export interface OrientationBandsDto {
    gutUpper: number;
    maessigLower: number;
    maessigUpper: number;
}

/** One Zielklasse's derivation: the energy-saving point and the renovated value. */
export interface TargetClassScenarioDto {
    /** Energy-saving point in percent, 1 decimal. */
    energySavingPointPercent: number;
    /** orientationValue × (1 − point), kWh/(m²a), 1 decimal — before the upper-limit margin. */
    renovatedValue: number;
}

/** The Prio Zielklasse's derivation: the H1 point × the Prio set's energy share of the total impact. */
export interface PriorityTargetClassScenarioDto extends TargetClassScenarioDto {
    /** The Prio set's energy-saving share of the total impact, percent, 1 decimal. */
    energyShareOfTotalPercent: number;
}

/** `targetClassBasis` of `POST /calculate`: the inputs of both Zielklassen. */
export interface TargetClassBasisDto {
    /** Ist (Wärme + Strom) / NGF, kWh/(m²a), 1 decimal. */
    orientationValue: number;
    bandCategory: OrientationBandCategory;
    /** True when the DEFAULT bands were used (Gebäudetyp missing or not mapped). */
    defaultBands: boolean;
    bands: OrientationBandsDto;
    /** The factor on the renovated value that decides the class (1.1 = 10 % margin). */
    upperLimitMargin: number;
    /** "Alle empfohlenen Maßnahmen"; null without a prognosis run / H1 point. */
    all: TargetClassScenarioDto | null;
    /** "Nur Prio-Maßnahmen"; null exactly when `priorityTargetClassSystem`'s inputs are missing. */
    priority: PriorityTargetClassScenarioDto | null;
}

/** Response of `POST /calculate`: every `computed:*` key of the field mapping. */
export interface SteckbriefCalculationResultDto {
    /**
     * The NEWEST (max year) entry of the year-tagged `heatConsumption` list
     * (GEN-744), kWh/a — the print's Wärmeverbrauch value. Added by the backend
     * GEN-744 PR; until that deploys the field is absent from responses (nothing
     * renders it yet — consume defensively if that changes).
     */
    heatConsumptionKwh: number | null;
    /** The year that newest heat entry relates to (print label suffix, GEN-744). */
    heatConsumptionYear: number | null;
    /** Newest `electricityConsumption` entry, kWh/a (GEN-744). */
    electricityConsumptionKwh: number | null;
    /** The year that newest electricity entry relates to (GEN-744). */
    electricityConsumptionYear: number | null;
    specificHeatDemand: number | null;
    specificElectricityDemand: number | null;
    orientationValue: number | null;
    orientationClass: string | null;
    gaugePointerPct: number | null;
    /**
     * The PRINTED Zielklasse (print label): the advisor's changed stage
     * (`targetClassOverride`) when given, else the engine's `targetClassSystem`.
     */
    targetClass: string | null;
    /**
     * The engine's Zielklasse before any advisor change — the energy-saving point
     * on the orientation value, with the 10 % upper-limit margin. Optional: older
     * backends report only `targetClass`.
     */
    targetClassSystem?: string | null;
    renovationNeedBadge: string | null;
    proposedMeasures: ProposedMeasureDto[];
    selectedMeasures: MeasureKey[];
    priorityMeasures: string[];
    recommendedHeatingSystem: RecommendedHeatingSystemKey | null;
    heatingSystemOverride: RecommendedHeatingSystemKey | null;
    /**
     * Echo of the request's priority override, verbatim (null when none was
     * given) — the effective flags are in `proposedMeasures` and
     * `priorityMeasures` (GEN-754).
     */
    priorityOverride: MeasureKey[] | null;
    /**
     * The PRINTED ranges: the engine's, with the advisor's adjusted bounds applied (GEN-782).
     * Since GEN-788 all three come from the calibrated prognosis model (whole
     * percent points, t/a with 1 decimal, € to the nearest 100 — the cost saving
     * is the sheet v6's H3 and CAN be negative) — 0–0 for an empty selection,
     * null when the model cannot predict the metric (see `prognosisWarnings`).
     */
    energySavingPotentialRange: RangeDto | null;
    co2SavingRange: RangeDto | null;
    costSavingRange: RangeDto | null;
    /**
     * The engine's own ranges before any adjustment (GEN-782) — the read-only
     * Systemprognose. Optional: absent from backends predating GEN-782 (the
     * panel then treats the printed range as the system range).
     */
    energySavingPotentialSystemRange?: RangeDto | null;
    co2SavingSystemRange?: RangeDto | null;
    costSavingSystemRange?: RangeDto | null;
    /**
     * The "Nur Prio-Maßnahmen" column (GEN-775): the same Ersteinschätzung run on
     * the effective Prio set only. Since GEN-788 the energy and CO₂ of this
     * column are null BY DESIGN (the prognosis model per Prio subset follows in a
     * next step — the panel and the print show "–"); the cost range still follows
     * the Prio set through the former per-measure model (combined saving
     * fractions × metered consumption × standard prices) and is null only for an
     * empty one.
     *
     * `priorityTargetClass` is the PRINTED Prio Zielklasse: the advisor's changed
     * stage (`priorityTargetClassOverride`) when given, else
     * `priorityTargetClassSystem`.
     */
    priorityTargetClass?: string | null;
    /**
     * The engine's Prio Zielklasse (print label): the H1 energy-saving point ×
     * the Prio set's energy share on the orientation value, same bands and 10 %
     * margin as the Alle one. Null without a Prio distribution, H1 point or
     * orientation value. Optional: older backends report only `priorityTargetClass`.
     */
    priorityTargetClassSystem?: string | null;
    /**
     * How the Zielklassen are derived — the transparency block of the Prognose
     * panel. Null without an orientation value; absent from older backends.
     */
    targetClassBasis?: TargetClassBasisDto | null;
    priorityEnergySavingPotentialRange?: RangeDto | null;
    priorityCo2SavingRange?: RangeDto | null;
    priorityCostSavingRange?: RangeDto | null;
    fundingHeadlineRange: FundingHeadlineRangeDto | null;
    fundingProgrammeChips: string[];
    /**
     * The German Hinweis the Steckbrief prints under the funding rate (GEN-775):
     * the headline rule's print note (or the no-Land-programme fallback) +
     * "Stand: <Monat Jahr>; Änderungen vorbehalten." — bounded to the print's
     * three lines.
     */
    fundingNote: string | null;
    /**
     * Internal advisor notes, never printed (GEN-775): the matrix caveats of
     * every matched funding rule (English, verbatim), then "Stand: <ISO date>"
     * and "Funding-Matrix <version>". Added by the backend GEN-775 funding-Hinweis
     * PR — OPTIONAL until that deploys: consume defensively (absent renders
     * nothing).
     */
    fundingAdvisorNotes?: string[];
    federalState: string | null;
    /**
     * The assumptions the engines actually applied in THIS run (GEN-756), in
     * pipeline order (facts mapping → measure rules → Ersteinschätzung
     * derivations) — empty when every value came from recorded data. Being added
     * by the parallel backend GEN-756 PR — OPTIONAL until that deploys: consume
     * defensively (absent means the backend does not report assumptions yet).
     */
    assumptions?: AssumptionDto[];
    /**
     * The EFFECTIVE window surface area, m² (GEN-774): a positive submitted
     * `windowSurfaceArea` value wins (user data, fires no assumption) → else
     * the derivation real surface × (submitted-or-default share) / 100 — null
     * when neither an explicit value nor a real surface area exists (the surface
     * is never guessed). On the derived path with the share absent or equal
     * to the Gebäudetyp's configured default, WINDOW_AREA_SHARE_DEFAULT (base
     * level) or WINDOW_AREA_SHARE_BUILDING_FUNCTION_DEFAULT (GEN-802) fires.
     * Display-only since the GEN-798 follow-up: the form always submits
     * windowSurfaceArea as null, shows this echo in "Assumptions & derived
     * values", and a save stamps it. OPTIONAL: consume defensively (absent
     * renders as a missing value).
     */
    windowSurfaceArea?: number | null;
    /**
     * The EFFECTIVE opaque facade surface area, m² (GEN-798): a positive
     * submitted `facadeSurfaceArea` wins → else the submitted real surface
     * area minus the effective window area, floored at 0 — null without a
     * real surface. Auto-fills the editable facadeSurfaceArea field like the
     * window area. OPTIONAL until the backend GEN-798 PR deploys.
     */
    facadeSurfaceArea?: number | null;
    /**
     * The CityGML building model's gross floor area in m² this run estimated the
     * NGF from (GEN-806) — set only on a SALES run without an entered NGF or
     * BGF and with an estimate for every building. Display-free: the form shows
     * the estimate only through the assumptions (the fields stay empty).
     */
    buildingModelGrossFloorArea?: number | null;
    /**
     * The hourly heat consumption, kW (GEN-780) — READ-ONLY, always derived
     * by the backend: newest heat consumption (kWh/a) ÷ the yearly heating
     * hours (h/a) of the building function's DIN V 18599-10 usage profile
     * (rounded to 1 decimal, fires HEAT_PUMP_POWER_ESTIMATED) → else null.
     * Not a form field and never submitted; the form does not read it (the
     * investment breakdown shows the heat pump's kW quantity instead).
     */
    heatPumpPower?: number | null;
    /**
     * Investment cost of ALL selected measures (GEN-780, print "für eine
     * Komplettsanierung"), EUR, each bound rounded half-up to 100,000 € (so the
     * print's "Mio. €" shows one decimal; a positive bound never below
     * 100,000 €) — null when the selection is empty or any selected measure is
     * not PRICED.
     * OPTIONAL until the backend GEN-780 PR deploys (absent reads as null).
     */
    investmentCostRange?: RangeDto | null;
    /** Same for the EFFECTIVE Prio set (GEN-780) — null for an empty Prio set. */
    priorityInvestmentCostRange?: RangeDto | null;
    /**
     * The effective Investitionskosten/m² (GEN-770 D3), €/m² — the advisor's
     * `investmentCostPerSqmOverrideLow/High` bounds over [investmentCostPerSqmSystemRange]. Without a
     * system range both bounds are needed; null = "k. A.".
     */
    investmentCostPerSqmRange?: RangeDto | null;
    /**
     * The system twin of [investmentCostPerSqmRange]: always null — the model produces no per-m²
     * figure and no lookalike figure ever enters the Prognose (GEN-700 D4).
     */
    investmentCostPerSqmSystemRange?: RangeDto | null;
    /**
     * "Investitionskosten gesamt (abgeleitet)" (GEN-770 D3), EUR — [investmentCostPerSqmRange] ×
     * the effective NGF, each bound rounded half-up to 10,000 €. Null without a per-m² range or an
     * NGF. Form only — the PDF keeps the item-priced [investmentCostRange].
     */
    investmentCostDerivedTotalRange?: RangeDto | null;
    /**
     * Whether the matching inputs changed since the stamped lookalike search (GEN-700 D4) — the
     * staleness hint „Vergleichswerte basieren auf geänderten Angaben — neu suchen". Null/absent
     * when the Variant carries no lookalike reference.
     */
    lookalikeReferenceStale?: boolean | null;
    /**
     * The prognosis model's warnings of THIS run (GEN-788), in display order.
     * Absent = a backend predating GEN-788 (the form renders no warnings block —
     * claiming "no warnings" would be dishonest); `[]` = computed, none fired
     * (also for an empty selection). The backend never sends null; the form
     * reads a null like absent.
     */
    prognosisWarnings?: PrognosisWarningDto[] | null;
    /**
     * The derivation behind the energy/CO₂/cost prognosis (GEN-788) — null when
     * no prognosis ran (empty selection), absent on older backends.
     */
    prognosis?: PrognosisDetailsDto | null;
    /**
     * The per-measure breakdown behind both totals (GEN-780): one line per
     * SELECTED measure, in the selection's impact order, `prio` = membership
     * in the effective Prio set. OPTIONAL until the backend deploys.
     */
    measureInvestments?: MeasureInvestmentDto[];
    /**
     * The PV Calculator estimate behind the PHOTOVOLTAICS investment line
     * (GEN-799): null when PV is not in the effective selection. Computed
     * synchronously inside `/calculate` on the first run per input set and
     * served from the server-side cache after — never in progress. OPTIONAL
     * until the backend deploys.
     */
    pvEstimate?: PvEstimateDto | null;
}

/**
 * The printed Dok-Nr. of a Variant (GEN-786) — mirror of the backend
 * `documentNumber`: the Steckbrief code plus the two-digit padded Variant number,
 * "SB-042" + 3 → "SB-042-03". Null while the number is unknown.
 */
export function variantDocumentNumber(steckbriefCode: string, variantNumber: number | undefined): string | null {
    if (variantNumber === undefined) return null;
    return `${steckbriefCode}-${String(variantNumber).padStart(2, '0')}`;
}

// ---------------------------------------------------------------------------
// Lookalike decision support (D4, GEN-700) — `POST /v1/ensan/steckbriefe/{id}/lookalikes`.
// The request is the `/calculate` request; the response is the Gebäudevergleich panel.
// Every figure is card-joined or deterministically derived; verdicts are enum values that
// passed the backend sanity gate. Mirrors the backend Kotlin DTOs (api-contracts.md).
// ---------------------------------------------------------------------------

/** Whether the search found two comparable buildings or none (kein Treffer). */
export type LookalikeSearchStatus = 'OK' | 'NO_MATCH';

/** The 12 matching Merkmale, in table order — the backend `Merkmal` wire keys. */
export type MerkmalKey =
    | 'BUILDING_USE'
    | 'YEAR_BUILT'
    | 'NET_FLOOR_AREA_M2'
    | 'LISTED_BUILDING'
    | 'ROOF'
    | 'FACADE'
    | 'BASEMENT_CEILING'
    | 'WINDOWS_RENEWED_YEAR'
    | 'GLAZING'
    | 'LIGHTING'
    | 'HEATING_ENERGY_SOURCE'
    | 'HEAT_GENERATOR_YEAR';

/** ✓ Trifft zu / ● Annähernd / ✕ Abweichung — null for Fenster erneuert and when the model gave none. */
export type MerkmalVerdict = 'TRIFFT_ZU' | 'ANNAEHERND' | 'ABWEICHUNG';

/** The three deterministic Prognose metrics compared against the lookalikes' actuals. */
export type LookalikePrognoseMetricKey = 'ENERGY_SAVING_PCT' | 'CO2_SAVING_T_A' | 'COST_SAVING_EUR_A';

/** Where a lookalike's actual falls relative to the frozen system range. */
export type RangeStatus = 'INSIDE' | 'NEAR' | 'OUTSIDE';

/** One subject Merkmal (first column of the Gebäudevergleich). */
export interface SubjectMerkmalDto {
    merkmal: MerkmalKey;
    value: unknown;
}

/** One gated Merkmal cell for a candidate. */
export interface CandidateMerkmalDto {
    merkmal: MerkmalKey;
    /** The card value shown (may carry a source bracket, e.g. "(aus U-Wert)"). */
    value: unknown;
    /** Enforced verdict — null for Fenster erneuert and when the model returned none. */
    verdict: MerkmalVerdict | null;
    reason: string | null;
    /** True when the verdict is TRIFFT_ZU because the values are identical (gate), not the model. */
    autoConfirmed: boolean;
}

export interface LookalikeCandidateDto {
    rank: number;
    docId: string;
    /** Server-derived label (property name + municipality) — never client-supplied. */
    label: string;
    propertyName: string | null;
    municipality: string | null;
    /** Report URL joined server-side from the card. */
    reportUrl: string | null;
    /** The compared Maßnahmenpaket — the search's suggestion until the advisor picks another. */
    packageId: string | null;
    packageName: string | null;
    /** The package the search suggested (the picker's „Vorschlag"). Absent on an older backend. */
    suggestedPackageId?: string | null;
    /** Every package of the lookalike's card, figures precomputed server-side — the picker's options. */
    packages?: CandidatePackageDto[];
    merkmale: CandidateMerkmalDto[];
    /** The lookalike's comparable (de-buffered) investment €/m². */
    investmentCostPerSquareMetre: number | null;
    /** The lookalike's comparable (de-buffered) total investment €, card-joined. */
    investmentCostTotal?: number | null;
    costBufferIncluded: boolean;
    dataQuality: Record<string, string>;
    caveats: string[];
    notes: string[];
    confidence: number | null;
}

/** One Maßnahmenpaket of a lookalike with its server-computed figures (the picker's option). */
export interface CandidatePackageDto {
    packageId: string;
    name: string | null;
    investmentCostPerSquareMetre: number | null;
    investmentCostTotal?: number | null;
    outcomes: { metric: LookalikePrognoseMetricKey; actual: number | null; status: RangeStatus | null }[];
}

/**
 * Request of `POST …/lookalikes/package-selection`: the comparison the panel shows (a fresh search's
 * or the Variant's stamped one) and docId → packageId. Stateless — no search job needed.
 */
export interface LookalikePackageSelectionDto {
    comparison: LookalikeComparisonDto;
    packages: Record<string, string>;
}

/** One lookalike's actual outcome for a metric and where it falls relative to the system range. */
export interface CandidateOutcomeDto {
    docId: string;
    rank: number;
    actual: number | null;
    status: RangeStatus | null;
}

/** One Prognose metric band: the frozen system range and each lookalike's actual + flag. */
export interface PrognoseMetricBandDto {
    metric: LookalikePrognoseMetricKey;
    systemRange: RangeDto | null;
    candidates: CandidateOutcomeDto[];
}

export interface LookalikeVersionsDto {
    prompt: string | null;
    model: string | null;
    index: string | null;
}

/** Job status of an async lookalike search (D4, GEN-700). */
export type LookalikeJobStatus = 'PENDING' | 'READY' | 'FAILED';

/** Response of `POST /v1/ensan/steckbriefe/{id}/lookalikes` — 202 Accepted, jobId to poll. */
export interface LookalikeJobAcceptedDto {
    jobId: string;
}

/** Response of `GET /v1/ensan/steckbriefe/{id}/lookalikes/{jobId}` — job state + result when READY. */
export interface LookalikeJobResultDto {
    jobId: string;
    status: LookalikeJobStatus;
    result: LookalikeComparisonDto | null;
    errorCode: string | null;
}

/** The Gebäudevergleich result — carried in `LookalikeJobResultDto.result` when READY. */
export interface LookalikeComparisonDto {
    searchId: string | null;
    status: LookalikeSearchStatus;
    /** German, present only on NO_MATCH. */
    noMatchReason: string | null;
    subject: SubjectMerkmalDto[];
    /** Exactly two on OK (ranked), empty on NO_MATCH. */
    candidates: LookalikeCandidateDto[];
    prognoseBand: PrognoseMetricBandDto[];
    /** Investitionskosten/m² aggregation (min–max over the lookalikes' de-buffered €/m²); derived, unflagged. */
    investmentCostPerSquareMetreRange: RangeDto | null;
    /** Reference-context fingerprint the Variant stamps (D4 lineage); null on NO_MATCH. */
    matchFingerprint: string | null;
    /** Subject part of the fingerprint (server-side recomputation only); null on NO_MATCH. */
    specsHash?: string | null;
    versions: LookalikeVersionsDto | null;
    /**
     * The backend's NEAR tolerance of the ✓/●/✕ flags (`LookalikeComparisonConstants`) — used for the
     * one flag drawn client-side, the €/m² row against the advisor's live range.
     */
    nearRelativeTolerance?: number | null;
}
